import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { Container, Text, Sprite, Graphics } from "@pixi/react";
import { TextStyle, TextMetrics } from "pixi.js";
import * as PIXI from "pixi.js";
import SpineTextureExtractor from "../../../../utils/SpineTextureExtractor";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import anime from "animejs";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { useReelControl } from "../../../../Contexts/ReelControlContext";
import { getNewUITexture } from "../../../../assets";
import {
  FONT_CONFIG,
  TEXT_STYLE_CONFIG,
  PixiSettings,
  USER_INTERFACE_BUTTON_CONFIG,
} from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";
import { closeBonusBuy } from "../../../../State/actions";
import {
  selectBonusBuyActive,
  selectCurrency,
  selectBalance,
  selectIsSpinning,
  selectCounterAnimationFinished,
  selectCriticalAnimationPlaying,
  selectBonusBuyMultipliers,
  selectBonusBuyFeatureName,
  selectBonusBuyFeatures,
} from "../../../../State/selectors";
import { formatToCustomLocale } from "../../../../utilities";
import { useButtonBrightness } from "../../../../Hooks/useButtonBrightness";
import { triggerScalePress } from "../../../../Hooks/useButtonHover";
import BonusBetSelector from "../Displays/BonusBetSelector";

// ── Overlay ──────────────────────────────────────────────────────────────────
const OVERLAY_ALPHA = 0.7;
const OVERLAY_COLOR = 0x000000;
const OVERLAY_FADE_MS = 200;

// ── Layout config ───────────────────────────────────────────────────────────
// Card dimensions come from popup_frame_bonus texture (native width/height).
// All ratios below are relative to cardWidth so everything scales dynamically.
const POPUP_CONFIG = {
  // Ratios relative to cardWidth (cardWidth derived from texture)
  cardGapRatio: 0.05, // Gap between cards (landscape/desktop)
  cardGapPortraitRatio: 0.075, // Gap between cards (portrait)
  paddingTopRatio: 0.05, // Inner content padding (top)
  paddingBottomRatio: 0.07, // Inner content padding (bottom)
  titleFontRatio: 0.07, // Title font size
  // Description size. Lowered from 0.055 when the single-line fit was
  // replaced by the two-line one below: that fit no longer collapses the text,
  // so the ideal became the size actually drawn — and 0.055 was a ceiling
  // nothing ever reached. 0.044 is the ceiling Vladimir asked for, 2026-09-18:
  // clearly bigger than the ~14px the old fit produced, not a jump to 24.
  subtitleFontRatio: 0.044, // Subtitle font size
  titleSubGapRatio: 0.075, // Gap between title and subtitle
  imageBottomGapRatio: 0.053, // Gap below image
  // ── The volatility + xBet block, between the image and the BUY button ──
  //
  // From the new card design. NOTHING in the API describes volatility — it is
  // not in feature_options, the round, or anywhere else — so the rating is
  // authored (see VOLATILITY below) and is a marketing value, not a computed one.
  infoRowGapRatio: 0.035, // Between the two rows
  infoBlockGapRatio: 0.05, // Between the block and the BUY button
  volatilityDotRatio: 0.042, // Dot diameter — see VolatilityRow
  volatilityDotGapRatio: 0.022, // Gap between dots
  volatilityLabelGapRatio: 0.03, // Between the word and the first dot
  // The "Volatility ●●●●●" and "N x bet" rows. Their OWN ratio, not the
  // description's size — see infoFontSize.
  infoFontRatio: 0.044,
  buyButtonWidthRatio: 0.6, // Buy button width
  buyFontRatio: 0.069, // Buy button text
  priceFontRatio: 0.065, // Price text
  betSelectorGapRatio: 0.14, // Gap between bet selector and cards (landscape/desktop)
  betSelectorGapPortraitRatio: 0.29, // Gap between bet selector and cards (portrait)
  rowMaxWidthRatio: 0.92, // Max share of screen width a row of cards may use
  betSelectorScaleRatio: 0.575, // Bet selector size relative to popupScale
  closeButtonOffsetRatio: 0.05, // Close button offset (% of viewport height)

  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE_SMALL]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.76,
        yOffset: 0.45,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.075,
        yOffset: 0.0,
      },
    },
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.75,
        yOffset: 0.475,
        betSelectorScaleRatio: 0.55,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.075,
        yOffset: 0.0,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.5,
        yOffset: 0,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.3,
        yOffset: 0,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: { deviceMultiplier: 1.15, yOffset: 0.02 },
  },
};

// Measures `text` at `idealFontSize` (single line) and shrinks it (in 2px
// steps) so it fits within `maxWidth`, or returns the ideal size unshrunk.
// Shared by the title and description fit calculations below, and reused by
// the parent (to compute a size shared across both cards) and by BuyCard
// itself (fallback when no shared size is passed in).
const fitFontSizeToWidth = (text, idealFontSize, maxWidth, fontWeight = "bold") => {
  const tempStyle = new TextStyle({
    fontFamily: FONT_CONFIG.PRIMARY,
    fontSize: idealFontSize,
    fontWeight,
    wordWrap: false,
  });
  const metrics = TextMetrics.measureText(text, tempStyle);
  if (metrics.width > maxWidth) {
    const scale = maxWidth / metrics.width;
    return Math.round((idealFontSize * scale) / 2) * 2;
  }
  return idealFontSize;
};

/**
 * How many lines a card description may use before the font has to shrink.
 *
 * The old fit allowed ONE, implicitly: it measured with wordWrap off, so the
 * size it returned was whatever made the whole sentence fit on a single line.
 * That was invisible with Zeus's copy ("1 or more random features") and brutal
 * with the game rules' ("Get one of the other 3 Free Spins options at random")
 * — the longest description decided a size for all four cards, and the text
 * came out tiny. Vladimir, 2026-09-18.
 */
const DESC_MAX_LINES = 2;

/**
 * The largest size at which `text` WRAPS into at most `maxLines` rows.
 *
 * The honest measurement for a paragraph that is drawn with wordWrap on: it
 * asks the same question the renderer will. fitFontSizeToWidth above still
 * suits the TITLE, which is short and must stay on one line.
 *
 * Steps down in 2px, the same grain as the ideal sizes, and never scales up —
 * the ideal is the ceiling.
 */
const fitFontSizeToLines = (
  text,
  idealFontSize,
  maxWidth,
  maxLines = DESC_MAX_LINES,
  fontWeight = "500",
) => {
  for (let size = idealFontSize; size > 8; size -= 2) {
    const metrics = TextMetrics.measureText(
      text,
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: size,
        fontWeight,
        wordWrap: true,
        wordWrapWidth: maxWidth,
      }),
    );
    if (metrics.lines.length <= maxLines) return size;
  }
  return 8;
};

// Bonus tier definitions — prices come from Redux (populated by API init).
//
// Mad Labs has FOUR buy levels (GDD §10). `featureLevel` is the only field
// the API sees: it becomes `purchased_feature_level` on the spin request, so
// these four values are what actually decide which package is bought.
//
// ARTWORK IS NOW MAD LABS' OWN, one image per level, and it MATCHES the tier
// it buys — the purple card shows 7/8/9 with no pips, red the same spins with
// two, gold 8/9/10 with four, and the fourth shows one of each behind a "?".
// No per-card scale or offset: the four are a consistent set at about
// 306x210, so the layout's own fit rule sizes them and they cannot drift
// apart. The Zeus images had to be nudged individually because they were two
// unrelated pictures reused four times.
//
// TITLES AND DESCRIPTIONS are the game rules' own copy, 2026-09-18, keyed in
// public/translations/translations.json (game namespace). English only so far:
// a locale that has not been translated yet falls back to that file, so the
// card reads English rather than printing a raw key.
// ── Volatility: FIVE DOTS ON EVERY CARD ──
//
// Out of 5, authored here and never sent by the API — nothing in
// feature_options, the round or anywhere else describes volatility.
//
// It was a per-tier rating (3 / 4 / 5 / 5) until Vladimir checked it against
// the card design, 2026-09-18: all four cards are drawn with the full row lit.
// One constant rather than four equal numbers, so the row cannot drift apart
// again by editing one card.
const VOLATILITY = 5;

// const BONUS_TIER_CONFIGS = [
//   {
//     key: "fs_low",
//     volatility: VOLATILITY,
//     titleKey: "bonus_buy_free_spins_low_title",
//     descKey: "bonus_buy_free_spins_low_description",
//     featureLevel: "1", // Purple — 7/8/9 spins from Level 0
//     featureImage: "bonus_buy_1",
//   },
//   {
//     key: "fs_mid",
//     volatility: VOLATILITY,
//     titleKey: "bonus_buy_free_spins_mid_title",
//     descKey: "bonus_buy_free_spins_mid_description",
//     featureLevel: "2", // Red — 7/8/9 spins from Level 2
//     featureImage: "bonus_buy_2",
//   },
//   {
//     key: "fs_high",
//     volatility: VOLATILITY,
//     titleKey: "bonus_buy_free_spins_high_title",
//     descKey: "bonus_buy_free_spins_high_description",
//     featureLevel: "3", // Gold — 8/9/10 spins from Level 4, the top package
//     featureImage: "bonus_buy_3",
//   },
//   {
//     key: "fs_random",
//     volatility: VOLATILITY,
//     titleKey: "bonus_buy_mystery_free_spins_title",
//     descKey: "bonus_buy_mystery_free_spins_description",
//     featureLevel: "4", // Mystery — one of the three above, at random
//     featureImage: "bonus_buy_4",
//   },
// ];
// ── Cleopatra's Fire: the server's FOUR buys (init feature_multipliers,
// verified on the real dev server 2026-09-30) ── `featureName` becomes
// `purchased_feature`, `featureLevel` `purchased_feature_level`:
//   free spins    freespin_buy 1 (30×) · freespin_buy 2 (50×)
//   Hold & Win    bonus_buy 1 (35×)    · bonus_buy 2 (70×)
// Titles and art are still Mad Labs' — placeholders (Vladimir: "we dont care
// about that now").
const BONUS_TIER_CONFIGS = [
  {
    key: "fs_1",
    volatility: VOLATILITY,
    // titleKey: "bonus_buy_free_spins_low_title",
    // descKey: "bonus_buy_free_spins_low_description",
    // Both free spins cards the same for now (Vladimir, 2026-09-30).
    titleKey: "bonus_buy_free_spins_title",
    descKey: "bonus_buy_free_spins_description",
    featureName: "freespin_buy",
    featureLevel: "1",
    // featureImage: "bonus_buy_1", — Mad Labs art, removed (2026-09-30)
    // The scatter's own picture stands for Free Spins until the art arrives.
    symbolImage: "sc",
  },
  {
    key: "fs_2",
    volatility: VOLATILITY,
    // titleKey: "bonus_buy_free_spins_mid_title",
    // descKey: "bonus_buy_free_spins_mid_description",
    // Both free spins cards the same for now (Vladimir, 2026-09-30).
    titleKey: "bonus_buy_free_spins_title",
    descKey: "bonus_buy_free_spins_description",
    featureName: "freespin_buy",
    featureLevel: "2",
    // featureImage: "bonus_buy_2", — Mad Labs art, removed (2026-09-30)
    // The scatter's own picture stands for Free Spins until the art arrives.
    symbolImage: "sc",
  },
  {
    key: "hnw_1",
    volatility: VOLATILITY,
    // titleKey: "bonus_buy_free_spins_high_title",
    // descKey: "bonus_buy_free_spins_high_description",
    // Both Hold & Win cards the same for now (Vladimir, 2026-09-30).
    titleKey: "bonus_buy_hold_and_win_title",
    descKey: "bonus_buy_hold_and_win_description",
    featureName: "bonus_buy",
    featureLevel: "1",
    // featureImage: "bonus_buy_3", — Mad Labs art, removed (2026-09-30)
    // The coin's own picture stands for Hold & Win until the card art arrives.
    symbolImage: "c",
  },
  {
    key: "hnw_2",
    volatility: VOLATILITY,
    // titleKey: "bonus_buy_mystery_free_spins_title",
    // descKey: "bonus_buy_mystery_free_spins_description",
    // Both Hold & Win cards the same for now (Vladimir, 2026-09-30).
    titleKey: "bonus_buy_hold_and_win_title",
    descKey: "bonus_buy_hold_and_win_description",
    featureName: "bonus_buy",
    featureLevel: "2",
    // featureImage: "bonus_buy_4", — Mad Labs art, removed (2026-09-30)
    // The coin's own picture stands for Hold & Win until the card art arrives.
    symbolImage: "c",
  },
];

// ═════════════════════════════════════════════════════════════════════════════
// BuyCard — a single bonus option card with title, subtitle, image, buy button
// ═════════════════════════════════════════════════════════════════════════════
/**
 * The "Volatility ●●●●●" line — label and dots centred as one group.
 *
 * Split out so the measuring stays readable: the label's width is only known
 * once PIXI has measured the text, so the group's left edge is derived rather
 * than assumed. Positioning the two independently looked right in English and
 * drifted in every other language.
 *
 * @param {number} filled How many of TOTAL_DOTS are lit.
 */
const VolatilityRow = ({
  cx,
  y,
  filled,
  label,
  style,
  dotSize,
  dotGap,
  labelGap,
}) => {
  const TOTAL_DOTS = 5;

  // ── A TEXTURE, because this renderer has no antialiasing ──
  //
  // Stage.js runs the app with `antialias: false`, so ANY vector circle has
  // hard stair-stepped edges here however finely it is tessellated. That is
  // what the jagged dots were — not the segment count, which is what I chased
  // first. A sprite carries its own soft alpha edge and stays clean at any size.
  //
  // `button_buy_bonus_background` is the round yellow BUY BONUS button, 181x181
  // in user_interface.json — already the right shape and the right yellow, and
  // scaling it down to a dot keeps it crisp.
  const dotTexture = useMemo(
    () => getNewUITexture("button_buy_bonus_background"),
    [],
  );

  const labelW = useMemo(
    () => Math.ceil(TextMetrics.measureText(label, style).width),
    [label, style],
  );

  const dotsW = TOTAL_DOTS * dotSize + (TOTAL_DOTS - 1) * dotGap;
  const groupW = labelW + labelGap + dotsW;
  const left = cx - groupW / 2;
  const dotsLeft = left + labelW + labelGap;

  return (
    <Container y={y}>
      <Text text={label} style={style} x={left} anchor={[0, 0.5]} />
      {dotTexture &&
        Array.from({ length: TOTAL_DOTS }, (_, i) => (
          <Sprite
            key={i}
            texture={dotTexture}
            x={dotsLeft + i * (dotSize + dotGap) + dotSize / 2}
            y={0}
            width={dotSize}
            height={dotSize}
            anchor={0.5}
            // Lit dots keep the button's own yellow. Unlit are the same object
            // faded, so the row reads as one set rather than two shapes.
            alpha={i < filled ? 1 : 0.25}
          />
        ))}
    </Container>
  );
};

const BuyCard = ({
  tier,
  cardWidth,
  cardHeight,
  currentBet,
  currency,
  canAfford,
  onBuy,
  sharedTitleFontSize,
  sharedSubtitleFontSize,
  sharedTitleAreaHeight,
}) => {
  const { t } = useTranslation();
  const { buttonRef, handlePointerOver, handlePointerOut, cleanupFilters } =
    useButtonBrightness();

  const isProcessingRef = useRef(false);
  const buyButtonContainerRef = useRef(null);

  // Clean up filters on unmount
  useEffect(() => () => cleanupFilters(), [cleanupFilters]);

  // ── All dimensions derived from cardWidth ───────────────────────────────
  const paddingTop = cardWidth * POPUP_CONFIG.paddingTopRatio;
  const paddingBottom = cardWidth * POPUP_CONFIG.paddingBottomRatio;
  const padding = cardWidth * POPUP_CONFIG.paddingBottomRatio; // sides use bottom ratio
  const contentWidth = cardWidth - padding * 2;

  // ── Text styles ─────────────────────────────────────────────────────────
  const dpr = window.devicePixelRatio || 1;
  const idealTitleFontSize =
    Math.round((cardWidth * POPUP_CONFIG.titleFontRatio) / 2) * 2;
  const idealSubtitleFontSize =
    Math.round((cardWidth * POPUP_CONFIG.subtitleFontRatio) / 2) * 2;
  const titleSubGap = cardWidth * POPUP_CONFIG.titleSubGapRatio;

  // Auto-scale title/description font to fit within card content width. When
  // a shared size is provided (computed across all cards by the parent), use
  // it so every card's text renders at the same size — otherwise a long title
  // or description on one card shrinks while a short one on the other stays
  // at full size, making the two look mismatched.
  const ownTitleFontSize = useMemo(
    () =>
      fitFontSizeToWidth(
        t(tier.titleKey).toUpperCase(),
        idealTitleFontSize,
        contentWidth,
        "bold",
      ),
    [idealTitleFontSize, contentWidth, t, tier.titleKey],
  );
  const titleFontSize = sharedTitleFontSize ?? ownTitleFontSize;

  const ownSubtitleFontSize = useMemo(
    () =>
      fitFontSizeToLines(
        t(tier.descKey),
        idealSubtitleFontSize,
        contentWidth,
      ),
    [idealSubtitleFontSize, contentWidth, t, tier.descKey],
  );
  const subtitleFontSize = sharedSubtitleFontSize ?? ownSubtitleFontSize;
  const imageBottomGap = cardWidth * POPUP_CONFIG.imageBottomGapRatio;

  // ── The volatility + "N x bet" block ──
  //
  // ── Its OWN size, and no longer the description's ──
  //
  // It used to take subtitleFontSize so the two would read as one family. But
  // that size is FITTED to the longest description on any card, and these two
  // rows are short — "Volatility" and "100 x bet" never need shrinking. So a
  // long sentence on one card was dragging the whole block down with it, which
  // is what made these unreadable.
  //
  // A ratio of the card instead: every card shows them at the same size,
  // because the card width is the same, and no amount of copy can shrink them.
  const infoFontSize =
    Math.round((cardWidth * POPUP_CONFIG.infoFontRatio) / 2) * 2;
  const infoRowGap = cardWidth * POPUP_CONFIG.infoRowGapRatio;
  const infoBlockGap = cardWidth * POPUP_CONFIG.infoBlockGapRatio;
  const dotSize = cardWidth * POPUP_CONFIG.volatilityDotRatio;
  const dotGap = cardWidth * POPUP_CONFIG.volatilityDotGapRatio;
  const dotLabelGap = cardWidth * POPUP_CONFIG.volatilityLabelGapRatio;
  // Two text rows plus the gap between them. The dots are smaller than the
  // text, so the row height is the text's.
  const infoBlockH = infoFontSize * 2 + infoRowGap;

  const titleStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: titleFontSize,
        fontWeight: "bold",
        fill: 0xffffff,
        align: "center",
        wordWrap: true,
        wordWrapWidth: contentWidth,
        resolution: dpr,
        ...TEXT_STYLE_CONFIG.OUTLINE,
      }),
    [titleFontSize, contentWidth, dpr],
  );

  // Same family and outline as the subtitle — this block is supporting copy,
  // not a heading. Kept as its own style so its size can move independently.
  const infoStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: infoFontSize,
        // ── IDENTICAL to the description, weight included ──
        //
        // It first shipped bold, grey and OUTLINED — the outline read as a
        // shadow the description does not have. Then 600, one step heavier.
        // Settled on matching it exactly: same family, size, colour and weight,
        // so this block is plainly the description's continuation.
        fontWeight: "500",
        fill: 0xffffff,
        align: "center",
        resolution: dpr,
        // NO outline. The description carries none, and this must match it —
        // do not add TEXT_STYLE_CONFIG.OUTLINE here.
      }),
    [infoFontSize, dpr],
  );

  const subtitleStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: subtitleFontSize,
        fontWeight: "500",
        fill: 0xffffff,
        align: "center",
        wordWrap: true,
        wordWrapWidth: contentWidth,
        lineHeight: Math.round(subtitleFontSize * 1.2),
        resolution: dpr,
      }),
    [subtitleFontSize, contentWidth, dpr],
  );

  // ── Textures ────────────────────────────────────────────────────────────
  // const featureTexture = useMemo(
  //   () => PIXI.Assets.get(tier.featureImage),
  //   [tier.featureImage],
  // );
  // A card may show a SYMBOL's picture (its spine, trimmed) in place of an
  // art file — the Hold & Win cards' coin, until their art arrives.
  const featureTexture = useMemo(
    () =>
      tier.symbolImage
        ? SpineTextureExtractor.getTrimmedTexture(tier.symbolImage)
        : PIXI.Assets.get(tier.featureImage),
    [tier.featureImage, tier.symbolImage],
  );
  const buttonTexture = useMemo(() => getNewUITexture("button_bonus_card"), []);
  const buttonDisabledTexture = useMemo(
    () => getNewUITexture("button_bonus_card_disabled"),
    [],
  );
  const cardBgTexture = useMemo(
    () => getNewUITexture("bonus_card_background"),
    [],
  );

  // ── Button config (all relative to cardWidth) ──────────────────────────
  const btnW = cardWidth * POPUP_CONFIG.buyButtonWidthRatio;
  const btnAR = buttonTexture
    ? buttonTexture.width / buttonTexture.height
    : 270 / 90;
  const btnH = btnW / btnAR;
  const buyFontSize =
    Math.round((cardWidth * POPUP_CONFIG.buyFontRatio) / 2) * 2;
  const priceFontSize =
    Math.round((cardWidth * POPUP_CONFIG.priceFontRatio) / 2) * 2;

  // A tier with no advertised price still has to be buyable — the server is
  // the authority on cost and will reject the purchase if the balance is
  // short. Blocking the button instead would make an unpriced tier unusable.
  const hasPrice = typeof tier.multiplier === "number" && tier.multiplier > 0;
  const cost = hasPrice ? currentBet * tier.multiplier : 0;
  const priceText = hasPrice
    ? `${formatToCustomLocale(cost)} ${currency}`
    : `— ${currency}`;

  // ── Auto-scale BUY + price text to fit button bounds ────────────────────
  const maxTextWidth = btnW * 0.9; // 90% of button width
  const maxTextHeight = btnH * 0.85; // 85% of button height for both texts combined
  const textGap = btnH * 0.05; // small gap between the two lines

  const { scaledBuyFontSize, scaledPriceFontSize } = useMemo(() => {
    const buyText = t("common:buy").toUpperCase();

    let bFS = buyFontSize;
    let pFS = priceFontSize;

    const measure = (text, size) => {
      const s = new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: size,
        fontWeight: "bold",
        wordWrap: true,
        wordWrapWidth: maxTextWidth,
      });
      return TextMetrics.measureText(text, s);
    };

    // First pass: scale each for width (widest single line)
    let buyM = measure(buyText, bFS);
    if (buyM.width > maxTextWidth) {
      bFS = Math.round((bFS * (maxTextWidth / buyM.width)) / 2) * 2;
    }
    let priceM = measure(priceText, pFS);
    if (priceM.width > maxTextWidth) {
      pFS = Math.round((pFS * (maxTextWidth / priceM.width)) / 2) * 2;
    }

    // Second pass: check combined height (accounts for wrapped multi-line text)
    buyM = measure(buyText, bFS);
    priceM = measure(priceText, pFS);
    const combinedH = buyM.height + textGap + priceM.height;
    if (combinedH > maxTextHeight) {
      const shrink = maxTextHeight / combinedH;
      bFS = Math.round((bFS * shrink) / 2) * 2;
      pFS = Math.round((pFS * shrink) / 2) * 2;
    }

    return { scaledBuyFontSize: bFS, scaledPriceFontSize: pFS };
  }, [
    buyFontSize,
    priceFontSize,
    priceText,
    maxTextWidth,
    maxTextHeight,
    textGap,
    t,
  ]);

  const buyTextStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: scaledBuyFontSize,
        fontWeight: "bold",
        fill: 0x000000,
        align: "center",
        wordWrap: true,
        wordWrapWidth: maxTextWidth,
        resolution: dpr,
      }),
    [scaledBuyFontSize, maxTextWidth, dpr],
  );

  const priceTextStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: scaledPriceFontSize,
        fontWeight: "bold",
        fill: 0x000000,
        align: "center",
        wordWrap: true,
        wordWrapWidth: maxTextWidth,
        resolution: dpr,
      }),
    [scaledPriceFontSize, maxTextWidth, dpr],
  );

  // ── Layout calculation (all relative to card frame) ────────────────────
  const titleText = t(tier.titleKey).toUpperCase();
  const subtitleText = t(tier.descKey);

  const titleMetrics = useMemo(
    () => TextMetrics.measureText(titleText, titleStyle),
    [titleText, titleStyle],
  );
  const subtitleMetrics = useMemo(
    () => TextMetrics.measureText(subtitleText, subtitleStyle),
    [subtitleText, subtitleStyle],
  );

  // Vertical layout: padding → title → gap → subtitle → image(fill) → button → padding
  const titleH = titleMetrics.height;
  const subtitleH = subtitleMetrics.height;
  const buttonAreaH = btnH;

  // Reserved title-row height. When one card's title wraps to more lines
  // than the other (both now share the same font size, but text length still
  // differs), reserving each card's own titleH would push its subtitle up
  // higher than the other card's — using the shared (tallest) height for
  // both keeps the subtitles vertically aligned across cards.
  const titleAreaH = sharedTitleAreaHeight ?? titleH;

  // Image gets remaining vertical space
  const usedH =
    paddingTop +
    titleAreaH +
    titleSubGap +
    subtitleH +
    titleSubGap +
    imageBottomGap +
    buttonAreaH +
    paddingBottom;
  const imageAvailH = Math.max(0, cardHeight - usedH);
  const imageAvailW = contentWidth * 0.92;

  // Scale image to fit available space
  const imageLayout = useMemo(() => {
    if (!featureTexture || imageAvailH <= 0) return null;
    const ar = featureTexture.width / featureTexture.height;
    let w, h;
    if (imageAvailH * ar <= imageAvailW) {
      h = imageAvailH;
      w = h * ar;
    } else {
      w = imageAvailW;
      h = w / ar;
    }
    const scale = tier.imageScale || 1;
    return { width: w * scale, height: h * scale };
  }, [featureTexture, imageAvailH, imageAvailW, tier]);

  // Y positions (all relative to card top-left origin)
  const cx = cardWidth / 2;
  // Title is vertically centered within the shared reserved area — so a
  // single-line title sits centered against a two-line title on the other
  // card, rather than pinned to the top of a taller-than-needed block.
  const titleY = paddingTop + titleAreaH / 2;
  const subtitleY = paddingTop + titleAreaH + titleSubGap + subtitleH / 2;
  // imageY = center of the available space region (between subtitle bottom and button top)
  const imageRegionTop =
    paddingTop + titleAreaH + titleSubGap + subtitleH + titleSubGap;
  // ── The image band now STOPS above the volatility block ──
  //
  // The new design puts two rows between the picture and the BUY button, so the
  // space the image is centred in has to give them up — otherwise the image
  // centres over them and the rows sit on top of it. This is the "lift the
  // images slightly upwards" half of that change, and it is subtractive rather
  // than a nudge, so it stays correct on every card size.
  const imageRegionBottom =
    cardHeight -
    paddingBottom -
    buttonAreaH -
    infoBlockGap -
    infoBlockH -
    imageBottomGap;
  const imageY =
    (imageRegionTop + imageRegionBottom) / 2 +
    cardHeight * (0.015 + (tier.imageYOffset || 0));
  const buttonY = cardHeight - paddingBottom - buttonAreaH / 2;
  // Bottom row first, then the volatility row above it — measured up from the
  // button so the block always sits against it however tall the card is.
  const xBetY =
    cardHeight - paddingBottom - buttonAreaH - infoBlockGap - infoFontSize / 2;
  const volatilityY = xBetY - infoFontSize / 2 - infoRowGap - infoFontSize / 2;

  // ── Handlers ────────────────────────────────────────────────────────────
  const handleBuyClick = useCallback(() => {
    if (!canAfford || isProcessingRef.current) return;
    isProcessingRef.current = true;

    soundManager.play(SOUND_NAME.button_click);
    if (buyButtonContainerRef.current)
      triggerScalePress(buyButtonContainerRef.current);

    onBuy(tier);

    setTimeout(() => {
      isProcessingRef.current = false;
    }, 500);
  }, [canAfford, onBuy, tier]);

  return (
    <Container name={`buy_card_${tier.key}`}>
      {/* Card background — popup_frame_bonus texture */}
      {cardBgTexture && (
        <Sprite
          texture={cardBgTexture}
          width={cardWidth}
          height={cardHeight}
          anchor={0}
        />
      )}

      {/* Title */}
      <Text
        text={titleText}
        style={titleStyle}
        anchor={0.5}
        roundPixels={true}
        x={cx}
        y={titleY}
      />

      {/* Subtitle */}
      <Text
        text={subtitleText}
        style={subtitleStyle}
        anchor={0.5}
        roundPixels={true}
        x={cx}
        y={subtitleY}
      />

      {/* Feature image */}
      {imageLayout && featureTexture && (
        <Sprite
          texture={featureTexture}
          anchor={0.5}
          roundPixels={true}
          x={cx}
          y={imageY}
          width={imageLayout.width}
          height={imageLayout.height}
        />
      )}

      {/* ── Volatility ──────────────────────────────────────────────────────

          The word and its dots on ONE line, centred together: the label is
          drawn from the middle of the group leftwards and the dots run
          rightwards from it, so the pair stays centred whatever the label
          translates to. Measured rather than positioned by hand for that
          reason — "Volatility" is not the same width in every language.

          Dots are drawn, not textured. The pagination dots this borrows from
          are CSS circles in PaytableSlider, so there is no asset to reuse —
          only the colours, which are taken from it: #ffc102 filled, white
          empty, matching the slider's active/inactive states. */}
      <VolatilityRow
        cx={cx}
        y={volatilityY}
        filled={tier.volatility || 0}
        label={t("bonus_buy_volatility")}
        style={infoStyle}
        dotSize={dotSize}
        dotGap={dotGap}
        labelGap={dotLabelGap}
      />

      {/* ── "N x bet" ──
          The multiplier is REAL — feature_options.feature_multipliers, one per
          level, parsed at init. Only the volatility above it is authored. A
          level the API did not advertise renders nothing rather than a wrong
          or zero number, the same rule the price already follows. */}
      {hasPrice && (
        <Text
          // ── Built in JS, NOT through i18n interpolation ──
          //
          // Two attempts at t() dropped the number: positionally
          // (`t(key, "{{n}} x bet", { n })`) it does not interpolate the
          // fallback, and with `defaultValue` in the options it still rendered
          // without it here. The multiplier is the whole point of the line, so
          // it is concatenated and only the WORDS are translated.
          text={`${tier.multiplier} ${t("bonus_buy_x_bet")}`}
          style={infoStyle}
          x={cx}
          y={xBetY}
          anchor={0.5}
        />
      )}

      {/* BUY button */}
      {buttonTexture && (
        <Container
          ref={buyButtonContainerRef}
          name={`buy_button_${tier.key}`}
          x={cx}
          y={buttonY}
        >
          <Sprite
            ref={buttonRef}
            texture={
              canAfford
                ? buttonTexture
                : (buttonDisabledTexture ?? buttonTexture)
            }
            anchor={0.5}
            width={btnW}
            height={btnH}
            eventMode={canAfford ? "static" : "none"}
            cursor={canAfford ? "pointer" : "default"}
            onpointertap={handleBuyClick}
            onpointerover={canAfford ? handlePointerOver : null}
            onpointerout={canAfford ? handlePointerOut : null}
          />
          {/* BUY text */}
          <Text
            text={t("common:buy").toUpperCase()}
            style={buyTextStyle}
            anchor={{ x: 0.5, y: 1 }}
            roundPixels={true}
            x={0}
            y={-btnH * 0.001}
            eventMode="none"
          />
          {/* Price text */}
          <Text
            text={priceText}
            style={priceTextStyle}
            anchor={{ x: 0.5, y: 0 }}
            roundPixels={true}
            x={0}
            y={btnH * 0.001}
            eventMode="none"
            alpha={0.6}
          />
        </Container>
      )}
    </Container>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// BuyBonusPopup — full-screen popup with two bonus tier cards
// ═════════════════════════════════════════════════════════════════════════════
const BuyBonusPopup = () => {
  const dispatch = useDispatch();
  const {
    dimensions,
    device,
    orientation,
    symbolScale,
    isMobileSmall,
    getResponsiveConfig,
    uiSymbolSize,
  } = useDimensions();
  const { handleSpin } = useReelControl();

  const isOpen = useSelector(selectBonusBuyActive);
  const currentBet = useSelector((state) => state.game.bet);
  const currency = useSelector(selectCurrency);
  const balance = useSelector(selectBalance);
  const bonusBuyMultipliers = useSelector(selectBonusBuyMultipliers);
  // The feature name the API advertised its prices under. Sending Zeus's
  // hardcoded "bonus_buy" to Mad Labs is rejected with 203 invalid_options.
  const bonusBuyFeatureName = useSelector(selectBonusBuyFeatureName);
  // Every feature's prices — each card reads its own feature's level.
  const bonusBuyFeatures = useSelector(selectBonusBuyFeatures);

  // Price each tier from its own level. Levels the API did not advertise stay
  // undefined and the card renders "—" rather than a wrong or zero price.
  const BONUS_TIERS = useMemo(
    () =>
      BONUS_TIER_CONFIGS.map((config) => ({
        ...config,
        // multiplier: bonusBuyMultipliers?.[config.featureLevel],
        multiplier:
          (config.featureName &&
            bonusBuyFeatures?.[config.featureName]?.[config.featureLevel]) ??
          bonusBuyMultipliers?.[config.featureLevel],
      })),
    [bonusBuyMultipliers, bonusBuyFeatures],
  );

  // Spin guards
  const isSpinning = useSelector(selectIsSpinning);
  const counterAnimationFinished = useSelector(selectCounterAnimationFinished);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const spinComplete = useSelector((state) => state.spin.spinComplete);
  const currentRound = useSelector((state) => state.game.currentRound);

  // Animation state
  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const popupContentRef = useRef(null);
  const buyConfirmedRef = useRef(false);
  const selectedTierRef = useRef(null);

  // Scroll state (mobile portrait only)
  const [scrollY, setScrollY] = useState(0);
  const isDragging = useRef(false);
  const lastTouchY = useRef(0);
  const scrollContentRef = useRef(null);
  const viewportMaskRef = useRef(null);

  // Close button hover/press effects
  const {
    buttonRef: closeButtonRef,
    handlePointerOver: closePointerOver,
    handlePointerOut: closePointerOut,
    triggerPress: closeTriggerPress,
    cleanupFilters: closeCleanupFilters,
  } = useButtonBrightness();

  useEffect(() => () => closeCleanupFilters(), [closeCleanupFilters]);

  // ── Responsive scale (symbolScale × deviceMultiplier) ──────────────────
  const popupScale = useMemo(() => {
    const realDevice = isMobileSmall
      ? PixiSettings.DEVICE.MOBILE_SMALL
      : device;
    const cfg =
      POPUP_CONFIG.DEVICE_CONFIGS[realDevice]?.[orientation] ||
      POPUP_CONFIG.DEVICE_CONFIGS[realDevice] ||
      POPUP_CONFIG.DEVICE_CONFIGS[PixiSettings.DEVICE.DESKTOP];
    const dm = cfg?.deviceMultiplier ?? 1.0;
    return symbolScale * dm;
  }, [device, orientation, symbolScale, isMobileSmall]);

  // ── Layout mode (column only for mobile portrait, row for everything else) ──
  const isColumnLayout =
    orientation === PixiSettings.ORIENTATION.PORTRAIT &&
    (device === PixiSettings.DEVICE.MOBILE || isMobileSmall);

  // ── Card dimensions (pinned to original 461×611 reference — texture scales to fit) ────
  //
  // Four cards in a row are far wider than the two this popup was built for,
  // so the row is measured against the screen and the whole set shrinks to
  // fit. Without this the outer cards run off both edges. The column layout
  // is unaffected — it grows downward and scrolls instead.
  const cardCount = BONUS_TIER_CONFIGS.length;
  const baseCardWidth = 461 * popupScale;
  const rowFitScale = useMemo(() => {
    if (isColumnLayout) return 1;
    const gap = baseCardWidth * POPUP_CONFIG.cardGapRatio;
    const rowWidth = cardCount * baseCardWidth + (cardCount - 1) * gap;
    const available = dimensions.width * POPUP_CONFIG.rowMaxWidthRatio;
    return rowWidth > available ? available / rowWidth : 1;
  }, [isColumnLayout, baseCardWidth, cardCount, dimensions.width]);

  const cardWidth = baseCardWidth * rowFitScale;
  const cardHeight = 611 * popupScale * rowFitScale;

  // ── Shared title/description font sizes — both cards use the smaller of
  // the two "would-fit" sizes, so a long title or description on one card
  // doesn't make it look mismatched against a short (unshrunk) one on the
  // other. ────────────────────────────────────────────────────────────────
  const { t } = useTranslation();
  const cardContentWidth = useMemo(() => {
    const padding = cardWidth * POPUP_CONFIG.paddingBottomRatio;
    return cardWidth - padding * 2;
  }, [cardWidth]);

  const titleFontSize = useMemo(() => {
    const idealTitleFontSize =
      Math.round((cardWidth * POPUP_CONFIG.titleFontRatio) / 2) * 2;
    return Math.min(
      ...BONUS_TIER_CONFIGS.map((config) =>
        fitFontSizeToWidth(
          t(config.titleKey).toUpperCase(),
          idealTitleFontSize,
          cardContentWidth,
          "bold",
        ),
      ),
    );
  }, [cardWidth, cardContentWidth, t]);

  const subtitleFontSize = useMemo(() => {
    const idealSubtitleFontSize =
      Math.round((cardWidth * POPUP_CONFIG.subtitleFontRatio) / 2) * 2;
    return Math.min(
      ...BONUS_TIER_CONFIGS.map((config) =>
        fitFontSizeToLines(
          t(config.descKey),
          idealSubtitleFontSize,
          cardContentWidth,
        ),
      ),
    );
  }, [cardWidth, cardContentWidth, t]);

  // ── Shared title-row height — both cards reserve the taller of the two
  // titles' wrapped heights (at the shared font size above), so if one
  // title wraps to more lines than the other, the descriptions below still
  // line up at the same Y instead of the shorter title's card sitting higher. ──
  const titleAreaHeight = useMemo(() => {
    const heights = BONUS_TIER_CONFIGS.map((config) => {
      const style = new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: titleFontSize,
        fontWeight: "bold",
        wordWrap: true,
        wordWrapWidth: cardContentWidth,
      });
      return TextMetrics.measureText(
        t(config.titleKey).toUpperCase(),
        style,
      ).height;
    });
    return Math.max(...heights);
  }, [titleFontSize, cardContentWidth, t]);

  // ── Center coordinates ────────────────────────────────────────────────
  const deviceConfig = useMemo(() => {
    const realDevice = isMobileSmall
      ? PixiSettings.DEVICE.MOBILE_SMALL
      : device;
    const configs = POPUP_CONFIG.DEVICE_CONFIGS;
    if (realDevice === PixiSettings.DEVICE.DESKTOP) {
      return configs[PixiSettings.DEVICE.DESKTOP] || {};
    }
    return configs[realDevice]?.[orientation] || {};
  }, [device, orientation, isMobileSmall]);

  // ── Bet selector scale (tweakable independently from cards) ────────────
  const betSelectorScale =
    popupScale *
    (deviceConfig.betSelectorScaleRatio ?? POPUP_CONFIG.betSelectorScaleRatio);

  const centerX = dimensions.width / 2;
  const centerY =
    dimensions.height / 2 + (deviceConfig.yOffset || 0) * cardHeight;

  const cardGap =
    cardWidth *
    (isColumnLayout
      ? POPUP_CONFIG.cardGapPortraitRatio
      : POPUP_CONFIG.cardGapRatio);

  // ── Bet selector position (above cards) ───────────────────────────────
  const betSelectorGap =
    cardWidth *
    (isColumnLayout
      ? POPUP_CONFIG.betSelectorGapPortraitRatio
      : POPUP_CONFIG.betSelectorGapRatio);
  const betSelectorY = useMemo(() => {
    if (isColumnLayout) {
      return centerY - cardHeight - cardGap / 2 - betSelectorGap;
    }
    return centerY - cardHeight / 2 - betSelectorGap;
  }, [centerY, cardHeight, cardGap, betSelectorGap, isColumnLayout]);

  // ── Close button size (same as ButtonSound / generic UI buttons) ───────
  const closeButtonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );
  const closeButtonSizeMultiplier = useMemo(() => {
    if (device === PixiSettings.DEVICE.DESKTOP) return 0.8;
    if (
      device === PixiSettings.DEVICE.MOBILE &&
      orientation === PixiSettings.ORIENTATION.PORTRAIT &&
      !isMobileSmall
    )
      return 0.45;
    return 0.5;
  }, [device, orientation, isMobileSmall]);
  const closeButtonSize = closeButtonConfig.width;

  // ── Close button position (mirrors ButtonSound padding per device) ──────
  const MAX_TOOLBAR_WIDTH = 1920;
  const closeButtonPos = useMemo(() => {
    const buttonVisualSize = closeButtonSize * closeButtonSizeMultiplier;

    // Y aligned with BET title (betSelectorY + label offset above the bet background)
    const betLabelY = betSelectorY - (popupScale * 110) / 2 - popupScale * 15;

    if (device === PixiSettings.DEVICE.MOBILE || isMobileSmall) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        const closeYOffset = isMobileSmall
          ? -cardHeight * 0.05
          : -cardHeight * 0.07;
        const closeXMultiplier = isMobileSmall ? 1.4 : 1.2;
        return {
          x: dimensions.width - buttonVisualSize * closeXMultiplier,
          y: betLabelY + buttonVisualSize * 0.5 + closeYOffset,
        };
      }
      // Mobile landscape — position to the right of cards, halfway to toolbar edge
      const card2RightEdge = centerX + cardGap / 2 + cardWidth;
      const toolbarWidth = Math.min(dimensions.width, uiSymbolSize * 10);
      const toolbarEdge = (dimensions.width + toolbarWidth) / 2;
      return {
        x: (card2RightEdge + toolbarEdge) / 2,
        y: closeButtonSize * 0.8,
      };
    }

    // Tablet & Desktop — toolbar capped at 1920px
    const toolbarWidth = Math.min(dimensions.width, MAX_TOOLBAR_WIDTH);
    const toolbarOffset = (dimensions.width - toolbarWidth) / 2;

    if (device === PixiSettings.DEVICE.TABLET) {
      return {
        x: toolbarOffset + toolbarWidth - closeButtonSize * 0.8,
        y: closeButtonSize * 0.8,
      };
    }
    // Desktop
    return {
      x: toolbarOffset + toolbarWidth - closeButtonSize,
      y: closeButtonSize * 1.4,
    };
  }, [
    dimensions.width,
    device,
    orientation,
    isMobileSmall,
    closeButtonSize,
    closeButtonSizeMultiplier,
    uiSymbolSize,
    betSelectorY,
    popupScale,
    centerX,
    cardGap,
    cardWidth,
    cardHeight,
  ]);

  // ── Card positions — one entry per tier ───────────────────────────────
  // Row in landscape, column in portrait, both centred on (centerX, centerY).
  // Built as a list so the card count is data: adding a fifth tier needs no
  // layout change here.
  const cardPositions = useMemo(() => {
    const count = BONUS_TIERS.length;
    if (isColumnLayout) {
      // ── Hung UNDER the bet selector, not centred as a block ──
      //
      // Centring is what breaks on a card count this popup was not built for.
      // At two cards the block happened to start just below the selector; at
      // four it starts 229px ABOVE the top of the screen, and since `scrollY`
      // clamps to [0, maxScrollY] there is no way to scroll up and reach it.
      //
      // The selector's own position is untouched — this is the same first-card
      // top the centred form produced at two cards, arrived at from the anchor
      // above rather than from the middle:
      //
      //   centred: centerY - cardHeight - cardGap/2
      //   here:    betSelectorY + betSelectorGap        (identical, by
      //            betSelectorY's own definition)
      //
      // So two cards land pixel-for-pixel where they always did, and any
      // further card simply extends the column downward into the scroll.
      const top = betSelectorY + betSelectorGap;
      return BONUS_TIERS.map((_, i) => ({
        x: centerX - cardWidth / 2,
        y: top + i * (cardHeight + cardGap),
      }));
    }
    const totalW = count * cardWidth + (count - 1) * cardGap;
    const left = centerX - totalW / 2;
    return BONUS_TIERS.map((_, i) => ({
      x: left + i * (cardWidth + cardGap),
      y: centerY - cardHeight / 2,
    }));
  }, [
    BONUS_TIERS,
    centerX,
    centerY,
    cardWidth,
    cardHeight,
    cardGap,
    isColumnLayout,
    betSelectorY,
    betSelectorGap,
  ]);

  // An unpriced tier is always affordable from the client's point of view —
  // the server prices it and rejects if the balance is short.
  const canAffordTier = useCallback(
    (tier) => {
      const hasPrice =
        typeof tier.multiplier === "number" && tier.multiplier > 0;
      if (!hasPrice) return true;
      return balance >= currentBet * tier.multiplier;
    },
    [balance, currentBet],
  );

  // ── Handlers ──────────────────────────────────────────────────────────
  const handleClose = useCallback(() => {
    if (isClosing) return;
    soundManager.play(SOUND_NAME.button_click);
    if (closeButtonRef.current) closeTriggerPress();
    setIsClosing(true);
  }, [isClosing, closeButtonRef, closeTriggerPress]);

  const handleBuy = useCallback(
    (tier) => {
      if (!canAffordTier(tier)) return;
      const hasPrice =
        typeof tier.multiplier === "number" && tier.multiplier > 0;

      // Spin guards
      const hasWin = currentRound?.win && currentRound.win > 0;
      const winNotFullyDisplayed =
        hasWin && spinComplete && !counterAnimationFinished;
      if (isSpinning || winNotFullyDisplayed || criticalAnimationPlaying)
        return;

      // Mark confirmed and store selected tier for spin trigger on close
      buyConfirmedRef.current = true;
      selectedTierRef.current = { ...tier, hasPrice };
      setIsClosing(true);
    },
    [
      canAffordTier,
      currentRound,
      spinComplete,
      counterAnimationFinished,
      isSpinning,
      criticalAnimationPlaying,
    ],
  );

  // ── Scroll (mobile portrait only) ────────────────────────────────────────
  // Max scroll = how far the LAST card's bottom overshoots the screen.
  const lastCardPos = cardPositions[cardPositions.length - 1];
  const maxScrollY =
    isColumnLayout && lastCardPos
      ? Math.max(0, lastCardPos.y + cardHeight - dimensions.height + 40)
      : 0;

  const handleScrollPointerDown = useCallback(
    (e) => {
      if (!isColumnLayout) return;
      if (e.data.pointerType !== "touch" && e.data.pointerType !== "pen")
        return;
      isDragging.current = true;
      lastTouchY.current = e.data.global.y;
    },
    [isColumnLayout],
  );

  const handleScrollPointerMove = useCallback(
    (e) => {
      if (!isDragging.current || !isColumnLayout) return;
      if (e.data.pointerType !== "touch" && e.data.pointerType !== "pen")
        return;
      e.stopPropagation();
      const currentY = e.data.global.y;
      const delta = (lastTouchY.current - currentY) * 2.0;
      lastTouchY.current = currentY;
      setScrollY((prev) => Math.max(0, Math.min(prev + delta, maxScrollY)));
    },
    [isColumnLayout, maxScrollY],
  );

  const handleScrollPointerUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  // Reset scroll when popup closes or layout changes
  useEffect(() => {
    if (!isOpen) setScrollY(0);
  }, [isOpen]);
  useEffect(() => {
    setScrollY(0);
  }, [isColumnLayout]);

  // Apply viewport mask to clip scrollable content when scrolling is needed
  useEffect(() => {
    if (scrollContentRef.current) {
      if (isColumnLayout && maxScrollY > 0 && viewportMaskRef.current) {
        scrollContentRef.current.mask = viewportMaskRef.current;
      } else {
        scrollContentRef.current.mask = null;
      }
    }
  }, [isColumnLayout, maxScrollY]);

  // Wheel scroll handler (desktop)
  const handleScrollWheel = useCallback(
    (event) => {
      if (!isColumnLayout) return;
      const scrollSpeed = 30;
      const newScrollY =
        scrollY + (event.deltaY > 0 ? scrollSpeed : -scrollSpeed);
      const clampedScrollY = Math.max(0, Math.min(newScrollY, maxScrollY));
      if (clampedScrollY !== scrollY) {
        event.preventDefault();
        event.stopPropagation();
        setScrollY(clampedScrollY);
      }
    },
    [isColumnLayout, scrollY, maxScrollY],
  );

  // ── Open animation (overlay fade → content fade, no scale) ─────────────
  useEffect(() => {
    if (isOpen && !isClosing) {
      // Fade in overlay
      anime({
        targets: { alpha: 0 },
        alpha: OVERLAY_ALPHA,
        duration: OVERLAY_FADE_MS,
        easing: "easeOutQuad",
        update: (anim) => setOverlayAlpha(anim.animations[0].currentValue),
      });

      // Fade in content (slight delay after overlay)
      if (popupContentRef.current) {
        popupContentRef.current.alpha = 0;
        anime({
          targets: popupContentRef.current,
          alpha: 1,
          duration: 200,
          delay: 100,
          easing: "easeOutQuad",
        });
      }
    }
  }, [isOpen, isClosing]);

  // ── Close animation (content fade → overlay fade) ─────────────────────
  useEffect(() => {
    if (!isClosing) return;

    let overlayAnim = null;
    let contentAnim = null;

    // Fade out content
    if (popupContentRef.current) {
      contentAnim = anime({
        targets: popupContentRef.current,
        alpha: 0,
        duration: 200,
        easing: "easeInQuad",
      });
    }

    // Fade out overlay (slight delay after content)
    overlayAnim = anime({
      targets: { alpha: OVERLAY_ALPHA },
      alpha: 0,
      duration: OVERLAY_FADE_MS,
      delay: 100,
      easing: "easeInQuad",
      update: (anim) => setOverlayAlpha(anim.animations[0].currentValue),
      complete: () => {
        setOverlayAlpha(0);
        dispatch(closeBonusBuy());
        setIsClosing(false);

        // Trigger spin after popup is fully closed
        if (buyConfirmedRef.current) {
          buyConfirmedRef.current = false;
          const tier = selectedTierRef.current;
          selectedTierRef.current = null;
          handleSpin({
            // purchasedFeature: bonusBuyFeatureName || "bonus_buy",
            // The card's own feature — free spins or Hold & Win.
            purchasedFeature: tier?.featureName || bonusBuyFeatureName || "bonus_buy",
            purchasedLevel: tier?.featureLevel || "1",
            // Omitted when the level has no advertised price — sending NaN
            // here would poison the balance bookkeeping downstream.
            ...(tier?.hasPrice
              ? { bonusCost: currentBet * tier.multiplier }
              : {}),
          });
        }
      },
    });

    return () => {
      if (overlayAnim) overlayAnim.pause();
      if (contentAnim) contentAnim.pause();
    };
  }, [isClosing, dispatch, handleSpin, currentBet, bonusBuyFeatureName]);

  // ── Draw overlay ──────────────────────────────────────────────────────
  const drawOverlay = useCallback(
    (g) => {
      g.clear();
      g.beginFill(OVERLAY_COLOR);
      g.drawRect(0, 0, dimensions.width, dimensions.height);
      g.endFill();
    },
    [dimensions.width, dimensions.height],
  );

  // ── Early return ──────────────────────────────────────────────────────
  if (!isOpen && !isClosing) return null;

  return (
    <Container
      name="buy_bonus_popup"
      zIndex={10000}
      sortableChildren
      eventMode="static"
    >
      {/* Dark overlay — catches stray clicks, blocks game underneath */}
      <Graphics
        draw={drawOverlay}
        alpha={overlayAlpha}
        eventMode="static"
        zIndex={0}
      />

      {/* Animated content container */}
      <Container name="popup_content" ref={popupContentRef} zIndex={1}>
        {/* Scroll event catcher — fixed in screen space, hitArea never moves */}
        <Container
          name="scroll_event_catcher"
          eventMode={isColumnLayout ? "static" : "passive"}
          onpointerdown={handleScrollPointerDown}
          onpointermove={handleScrollPointerMove}
          onpointerup={handleScrollPointerUp}
          onpointercancel={handleScrollPointerUp}
          onpointerleave={handleScrollPointerUp}
          onwheel={handleScrollWheel}
          {...(isColumnLayout
            ? {
                hitArea: new PIXI.Rectangle(
                  0,
                  0,
                  dimensions.width,
                  dimensions.height,
                ),
              }
            : {})}
        >
          {/* Viewport mask — clips scrollable content to visible area */}
          {isColumnLayout && maxScrollY > 0 && (
            <Graphics
              ref={viewportMaskRef}
              name="viewport_mask"
              renderable={false}
              draw={(g) => {
                g.clear();
                g.beginFill(0x000000, 1);
                g.drawRect(0, 0, dimensions.width, dimensions.height);
                g.endFill();
              }}
            />
          )}

          {/* Scrollable content — only this moves */}
          <Container
            ref={scrollContentRef}
            name="scrollable_content"
            y={isColumnLayout ? -scrollY : 0}
          >
            {/* Bet selector — above cards */}
            <BonusBetSelector
              popupScale={betSelectorScale}
              position={{ x: centerX, y: betSelectorY }}
            />

            {/* One card per buy level */}
            {BONUS_TIERS.map((tier, i) => (
              <Container
                key={tier.key}
                x={cardPositions[i].x}
                y={cardPositions[i].y}
              >
                <BuyCard
                  tier={tier}
                  cardWidth={cardWidth}
                  cardHeight={cardHeight}
                  currentBet={currentBet}
                  currency={currency}
                  canAfford={canAffordTier(tier)}
                  onBuy={handleBuy}
                  sharedTitleFontSize={titleFontSize}
                  sharedSubtitleFontSize={subtitleFontSize}
                  sharedTitleAreaHeight={titleAreaHeight}
                />
              </Container>
            ))}
          </Container>
        </Container>

        {/* Close button — outside scroll area, always fixed */}
        <Sprite
          ref={closeButtonRef}
          name="popup_close_button"
          texture={getNewUITexture("button_x")}
          anchor={0.5}
          width={closeButtonSize * closeButtonSizeMultiplier}
          height={closeButtonSize * closeButtonSizeMultiplier}
          x={closeButtonPos.x}
          y={closeButtonPos.y}
          eventMode="static"
          cursor="pointer"
          onpointertap={handleClose}
          onpointerover={closePointerOver}
          onpointerout={closePointerOut}
        />
      </Container>
    </Container>
  );
};

export default BuyBonusPopup;
